/**
 * What a deep dive keeps about a company or product, beyond its checklist:
 * facts with where they were read, its other pages, and what people say for
 * and against it.
 *
 * Each section says plainly when it is empty, so an unexplored item reads as
 * "nobody has looked" rather than as a page that failed to load.
 */
import type { BoardroomClaim, BoardroomVoice, BoardroomVoices } from '../../../types';
import { VerdictBadge } from '../ItemCard';

const CARD =
  'shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3';
const HEADING = 'text-[11px] uppercase tracking-wide text-gray-400 mb-1';

const shortUrl = (url: string) => url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

export function Facts({ claims }: { claims: BoardroomClaim[] }) {
  return (
    <section className={CARD} data-testid="profile-facts">
      <h3 className={HEADING}>Facts · {claims.length}</h3>
      {claims.length === 0 ? (
        <span className="text-xs text-gray-400 italic">none recorded - a deep dive looks for them</span>
      ) : (
        <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-3 gap-y-1 text-xs">
          {claims.map(c => (
            <div key={c.name} className="contents">
              <dt className="text-gray-500 dark:text-gray-400">{c.name}</dt>
              <dd className="min-w-0 text-gray-800 dark:text-gray-100">
                {c.value}
                {c.unit && ` ${c.unit}`}
                {c.as_of && <span className="text-gray-400"> · as of {c.as_of}</span>}
                {c.previous && (
                  <span className="text-gray-400" title="An earlier figure this one replaced">
                    {' '}
                    · was {c.previous.value}
                    {c.previous.as_of && ` (${c.previous.as_of})`}
                  </span>
                )}
                {c.source && (
                  <>
                    {' '}
                    ·{' '}
                    {c.source.url ? (
                      <a
                        href={c.source.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:underline dark:text-indigo-400"
                        title={c.source.title}
                      >
                        source
                      </a>
                    ) : (
                      <span className="text-gray-400" title={c.source.title}>
                        source
                      </span>
                    )}
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

export function Links({ links }: { links: { label: string; url: string }[] }) {
  if (links.length === 0) return null;
  return (
    <section className={CARD} data-testid="profile-links">
      <h3 className={HEADING}>Links · {links.length}</h3>
      <ul className="flex flex-col gap-0.5 text-xs">
        {links.map(l => (
          <li key={l.label} className="flex min-w-0 gap-2">
            <span className="shrink-0 text-gray-500 dark:text-gray-400">{l.label}</span>
            <a
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 truncate text-indigo-600 hover:underline dark:text-indigo-400"
            >
              {shortUrl(l.url)}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Voice({ v, itemId, onOpen }: { v: BoardroomVoice; itemId?: number; onOpen: (id: number) => void }) {
  // ONCE, WITH EVERYTHING IT IS ABOUT. A quote filed under the company and
  // two of its products is one line naming the products, not three lines.
  // The page's own item is not repeated.
  const about = (v.about_all ?? [v.about]).filter(a => a.id !== itemId);
  return (
    <li className="text-xs">
      <button type="button" onClick={() => onOpen(v.id)} className="text-left text-gray-800 hover:underline dark:text-gray-100">
        {v.quote ? `“${v.quote}”` : v.title}
      </button>
      <span className="ml-1 text-[11px] text-gray-400">
        {about.length > 0 && `${about.map(a => a.title).join(', ')} · `}
        {v.url ? (
          <a href={v.url} target="_blank" rel="noreferrer" className="hover:underline">
            {shortUrl(v.url).split('/')[0]}
          </a>
        ) : (
          'no page'
        )}
        {v.quote_verified === false && ' · quote not found on the page'}
      </span>
      {v.verdict && (
        <span className="ml-1">
          <VerdictBadge verdict={v.verdict} />
        </span>
      )}
    </li>
  );
}

/** Praise and complaints side by side, each in the person's words. */
export function Voices({
  voices,
  itemId,
  onOpen,
}: {
  voices: BoardroomVoices;
  /** The page's own item: voices about it do not repeat its name. */
  itemId: number;
  onOpen: (id: number) => void;
}) {
  const column = (title: string, rows: BoardroomVoice[], empty: string, testId: string) => (
    <div data-testid={testId}>
      <h4 className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-300">
        {title} · {rows.length}
      </h4>
      {rows.length === 0 ? (
        <span className="text-xs italic text-gray-400">{empty}</span>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map(v => (
            <Voice key={v.id} v={v} itemId={itemId} onOpen={onOpen} />
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <section className={CARD} data-testid="profile-voices">
      <h3 className={HEADING}>What people say</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {column('Praise', voices.praise, 'none recorded yet', 'voices-praise')}
        {column('Complaints', voices.complaints, 'none recorded yet', 'voices-complaints')}
      </div>
    </section>
  );
}
